import { FC, ChangeEvent, useState, useEffect } from "react";
import { Modal, Button, InputGroup, Form } from "react-bootstrap";
import { useBucketContext } from "context/bucket";
import { MessageComponent } from "components/message";
import { showSuccessToast } from "utils";
import { useAppDispatch } from "redux/store";
import { showErrorModal } from "redux/slices";
import { CartellaModalPropTypes } from "./types";
import { isInvalidFileExtension, compareExtensions } from "./utils";
import { ConfirmationModal } from "components/confirmationModal";

export const CartellaModal: FC<CartellaModalPropTypes> = (props) => {
  const { closeModal, inputValue, sourcePath, isFolder, title, show } = props;
  const [item, setItem] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showExtInfoModal, setShowExtInfoModal] = useState(false);

  const {
    createFolder,
    breadcrumb,
    content,
    apiHandler,
    fetchHomePageData,
    renameFolder,
    renameFile,
  } = useBucketContext();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (inputValue) {
      setItem(inputValue);
    }
  }, [inputValue]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setItem(event.target.value.trim());
    setValidationError("");
  };
  const handleCreateFolder = async () => {
    const result = await createFolder(item);
    result && (await fetchHomePageData(breadcrumb));
    return result;
  };
  const handleRenameFolder = async () => {
    if (sourcePath) {
      const result = await renameFolder(`${breadcrumb}${item}/`, sourcePath);
      result && (await fetchHomePageData(breadcrumb));
      return result;
    }
    return;
  };
  const handleRenameFile = async () => {
    if (sourcePath) {
      const result = await renameFile(item, sourcePath);
      result && (await fetchHomePageData(breadcrumb));
      return result;
    }
    return;
  };
  const handleExtValidation = (item: string) => {
    const isInvalid = isInvalidFileExtension(item);
    if (isInvalid) {
      dispatch(
        showErrorModal({
          text: "L'estensione inserita non è permessa. Sono ammessi solo file con estensione jpg, jpeg, png, pdf, txt, doc, docx, xls, xlsx, odt, ods",
        })
      );
      closeModal();
    } else {
      setShowExtInfoModal(true);
    }
    return;
  };
  const executeOperation = async (
    handler: () => Promise<boolean | undefined>,
    successMessage: string,
    errorMessage: string
  ) => {
    const result = await apiHandler(handler);
    if (result) {
      showSuccessToast(successMessage, "success_folder_rename");
      setItem("");
    } else {
      dispatch(
        showErrorModal({
          text: errorMessage,
        })
      );
    }
    closeModal();
  };
  const handleOkClick = async () => {
    const error = getValidationError(item);
    if (error) {
      setValidationError(error);
    } else {
      if (inputValue && inputValue.length !== 0) {
        if (isFolder) {
          executeOperation(
            handleRenameFolder,
            "Cartella rinominata",
            "Errore durante la rinomina della cartella"
          );
        } else {
          const extChanged = compareExtensions(inputValue, item);
          if (extChanged) {
            handleExtValidation(item);
          } else {
            executeOperation(
              handleRenameFile,
              "File rinominata",
              "Errore durante la rinomina della file"
            );
          }
        }
      } else {
        executeOperation(
          handleCreateFolder,
          "Cartella creata",
          "Errore durante la creazione della cartella"
        );
      }
    }
  };
  const handleCloseModal = () => {
    setValidationError("");
    closeModal();
  };
  const getValidationError = (folderName: string) => {
    if (!folderName) {
      return "Devi inserire il nome...";
    } else if (folderName.indexOf("/") !== -1) {
      return "Il nome non può contenere il carattere '/'";
    } else if (checkDuplicate(folderName)) {
      return "Il nome è duplicato.";
    } else {
      return;
    }
  };
  const checkDuplicate = (folderName: string) =>
    !!content?.folderList?.find((folder) => folder.name === folderName);

  return (
    <>
      <Modal
        show={show}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="d-flex justify-content-center align-items-center border-bottom-0">
          <Modal.Title className="text-light fs-2 fw-bold text-center">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup>
            <Form.Control
              placeholder="Inserisci il nome della cartella"
              onChange={handleInputChange}
              value={item}
            />
          </InputGroup>
        </Modal.Body>
        <MessageComponent type="validationError" message={validationError} />
        <Modal.Footer className="d-flex justify-content-center align-items-center border-top-0 mt-2">
          <Button
            variant="outline-light px-4 rounded-1"
            onClick={handleCloseModal}
            style={{ width: 130 }}
          >
            Annulla
          </Button>
          <Button
            variant="secondary text-white px-4 rounded-1"
            onClick={handleOkClick}
            style={{ width: 130 }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      {showExtInfoModal && (
        <ConfirmationModal
          show={showExtInfoModal}
          text="Stai modificando l'estensione del file."
          title="Sei sicuro?"
          confirmButton="Si,rinomina!"
          onConfirm={() =>
            executeOperation(
              handleRenameFile,
              "File rinominata",
              "Errore durante la rinomina della file"
            )
          }
          onCancel={() => {
            setShowExtInfoModal(false);
            closeModal();
          }}
        />
      )}
    </>
  );
};
